import { FC, useState, useEffect } from 'react';
import { Page } from '@/components/Page.tsx';
import {
  Section,
  Cell,
  Button,
  Caption,
  Headline,
  Subheadline,
  Spinner,
  Avatar,
  Input, Modal, Blockquote,
} from '@telegram-apps/telegram-ui';
import { useTranslation } from 'react-i18next';
import { useSecretSanta } from '@/providers/SecretSantaProvider.tsx';
import { useAuthUser } from '@/providers';
import { useNavigation } from '@/providers/NavigationProvider.tsx';
import {TbCalendar, TbCoin, TbSend, TbEdit, TbShare3, TbPlayerPlay, TbDoorExit, TbChevronRight} from 'react-icons/tb';
import {getGameMessages, sendGameMessage, startDraw, leaveSecretSantaGame, kickParticipant} from '@/api/apiMethods.ts';
import {SecretSantaMessage, SecretSantaParticipant} from '@/api/types.ts';
import {
  ModalHeader
} from "@telegram-apps/telegram-ui/dist/components/Overlays/Modal/components/ModalHeader/ModalHeader";
import {AiOutlineUserDelete} from "react-icons/ai";
import {LuList} from "react-icons/lu";
import {renderWithHtml} from "@/helpers/renderWithHtml.tsx";
import {miniApp, useSignal} from "@telegram-apps/sdk-react";
import {haptic} from "@/helpers/haptic.ts";
import {getUserAcronym} from "@/helpers/getUserAcronym.ts";

export const SecretSantaGamePage: FC = () => {
  const { game, isLoading, refreshGame } = useSecretSanta();
  const { authUser } = useAuthUser();
  const { t, i18n: { language } } = useTranslation();
  const { navigateToRoute, showSnackbar } = useNavigation();
  const [messages, setMessages] = useState<SecretSantaMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<SecretSantaParticipant|null>(null)
  const isDark = useSignal(miniApp.isDark);

  useEffect(() => {
    refreshGame().then();
    if (game && (game.status === 'drawn' || game.status === 'completed')) {
      loadMessages().then();
    }
  }, [game?.id, game?.status]);

  const loadMessages = async () => {
    if (!game) return;
    try {
      const fetchedMessages = await getGameMessages(game.id);
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!game || !messageText.trim()) return;
    
    setIsSendingMessage(true);
    try {
      await sendGameMessage(game.id, messageText);
      setMessageText('');
      await loadMessages();
      haptic.notification('success');
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.messageFailed');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleStartDraw = async () => {
    if (!game) return;
    
    if (game.participants.length < 3) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.insufficientParticipants');
      return;
    }

    haptic.notification('warning');
    const confirmed = confirm(t('secretSanta.dialog.drawConfirm'));
    if (!confirmed) return;

    setIsDrawing(true);
    try {
      await startDraw(game.id);
      haptic.notification('success');
      showSnackbar('✅', 'secretSanta.status.drawn');
      window.location.reload();
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.drawFailed');
    } finally {
      setIsDrawing(false);
    }
  };

  const handleShareGame = async () => {
    if (!game) return;
    haptic.impact('light');
    const gameLink = `${import.meta.env.VITE_MINIAPP_URL}?startapp=-s${game.id}`;
    try {
      const { shareLink } = await import('@/helpers/shareLink');
      await shareLink(gameLink, t('secretSanta.share.shareText'));
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'system.unknowError');
    }
  };

  const handleLeaveGame = async () => {
    if (!game) return;
    
    haptic.notification('warning');
    const confirmed = confirm(t('secretSanta.dialog.leaveConfirm'));
    if (!confirmed) return;

    try {
      await leaveSecretSantaGame(game.id);
      haptic.notification('success');
      showSnackbar('✅', 'secretSanta.participants.leave');
      navigateToRoute('/main/secret-santa', true);
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'secretSanta.error.leaveFailed');
    }
  };

  if (isLoading) {
    return (
      <Page>
        <div className="flex justify-center items-center p-10">
          <Spinner size="l" />
        </div>
      </Page>
    );
  }

  if (!game) {
    return (
      <Page>
        <div className="p-10 text-center">
          <Caption className="text-color-hint">{t('secretSanta.error.loadFailed')}</Caption>
        </div>
      </Page>
    );
  }

  const isOrganizer = game.organizer_id === authUser?.id;
  const myParticipant = game.participants.find(p => p.user_id === authUser?.id);
  const isParticipant = !!myParticipant;

  const formatDate = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusText = (status: string) => {
    return t(`secretSanta.status.${status}`);
  };

  const bottomSheetOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedParticipant(null)
    }
  }

  const openParticipantModal = (participant: SecretSantaParticipant) => {
    if (isOrganizer || participant.user_id === authUser?.id) {
      haptic.impact('soft');
      setSelectedParticipant(participant);
    }
  }

  const kickSelectedParticipant = async () => {
    if (!game || !selectedParticipant) return;

    haptic.notification('warning');
    const confirmed = confirm(t('secretSanta.dialog.kickConfirm'));
    if (!confirmed) return;

    try {
      await kickParticipant(game.id, selectedParticipant.user_id);
      haptic.notification('success');
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', 'system.unknowError');
    } finally {
      setSelectedParticipant(null);
      await refreshGame();
    }
  }

  return (
    <Page>
      <div className="flex flex-col gap-3 pb-10">
        {/* Game Header */}
        <div className="p-4 bg-section rounded-b-xl flex flex-col gap-3">
          {/*{game.cover_image_id && (*/}
          {/*  <div className="w-full h-32 bg-gray-200 rounded-lg"></div>*/}
          {/*)}*/}
          <Headline weight="2" className={"flex items-center gap-2"}>
            {game.title}
            <Caption>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                {getStatusText(game.status)}
              </span>
            </Caption>
          </Headline>
          {game.description && (
            <Subheadline level="2" className="text-color-hint">
              {game.description}
            </Subheadline>
          )}
          <div className="flex flex-col gap-2">
            {game.budget && (
              <Caption className="flex items-center gap-2">
                <TbCoin size={16} />
                {t('secretSanta.form.budget') + ": "}
                {game.budget.number} {game.budget.currency}
              </Caption>
            )}
            {game.event_date && (
              <Caption className="flex items-center gap-2">
                <TbCalendar size={16} />
                {t('secretSanta.form.eventDate') + ": "}
                {formatDate(game.event_date)}
              </Caption>
            )}
          </div>
          {/* Organizer Actions */}
          {isOrganizer && (
            game.status === 'open' && (
              <div>
                <Button
                  stretched={true}
                  onClick={() => {
                    navigateToRoute(`/main/secret-santa/${game.id}/edit`);
                  }}
                  before={<TbEdit size={20} />}
                  mode="plain"
                >
                  {t('secretSanta.actions.edit')}
                </Button>
              </div>
            )
          )}
          <Button
            stretched={true}
            onClick={handleShareGame}
            after={<TbShare3 size={20} />}
            mode="plain"
          >
            {t('secretSanta.actions.share')}
          </Button>
        </div>

        {/* Join Button for Non-Participants */}
        {!isParticipant && game.status === 'open' && (
          <div className={"pr-3 pl-3"}>
            <Button
              stretched={true}
              onClick={() => {
                navigateToRoute(`/main/secret-santa/${game.id}/join`);
              }}
              mode="filled"
              after={<TbChevronRight size={20}/>}
            >
              {t('secretSanta.participants.join')}
            </Button>
          </div>
        )}

        {/* Participants Section */}
        <Section header={t('secretSanta.participants.title') + ` (${game.participants.length})`}>
          {myParticipant ? <Cell
            onClick={() => {openParticipantModal(myParticipant)}}
            key={myParticipant.user_id}
            before={<Avatar size={40} acronym={getUserAcronym(myParticipant.display_name)} />}
            className={"pb-3 pt-3"}
          >
            {myParticipant.display_name}
            <Caption className="tg-text-button-color"> ({t('secretSanta.participants.youParticipate')})</Caption>
          </Cell> : null}
          {game.participants.map((participant) => {
            return myParticipant?.user_id !== participant.user_id && <Cell
              onClick={() => {
                openParticipantModal(participant)
              }}
              key={participant.user_id}
              before={<Avatar size={40} acronym={getUserAcronym(participant.display_name)}/>}
              className={"pb-3 pt-3"}
            >
              {participant.display_name}
              {participant.user_id === authUser?.id && (
                <Caption className="tg-text-button-color"> ({t('secretSanta.participants.youParticipate')})</Caption>
              )}
            </Cell>
          })}
        </Section>

        {isOrganizer && (game.status === 'open' && <div className={"pl-3 pr-3"}>
          <Button
              stretched={true}
              onClick={handleStartDraw}
              before={<TbPlayerPlay size={20} />}
              mode="filled"
              disabled={isDrawing}
              loading={isDrawing}
            >
              {t('secretSanta.actions.startDraw')}
          </Button>
        </div>)}

        {/* Assignment Section */}
        {isParticipant && (game.status === 'drawn' || game.status === 'completed') && myParticipant?.receiver_id && (
          <Section header={t('secretSanta.assignment.title')}>
            {(() => {
              const receiver = game.participants.find(p => p.user_id === myParticipant.receiver_id);
              if (!receiver) return null;
              
              return (
                <>
                  <Cell
                    before={<Avatar size={40} acronym={getUserAcronym(receiver.display_name)} />}
                    description={receiver.preferences || t('secretSanta.assignment.noPreferences')}
                  >
                    {receiver.display_name}
                  </Cell>
                  {receiver.wishlist_id && (
                    <Button
                      stretched={true}
                      className="mb-1"
                      onClick={() => {
                        navigateToRoute(`/main/wishlists/${receiver.wishlist_id}`);
                      }}
                      mode="gray"
                    >
                      {t('secretSanta.assignment.viewWishlist')}
                    </Button>
                  )}
                </>
              );
            })()}
          </Section>
        )}

        {/* Messages Section */}
        {isParticipant && (game.status === 'drawn' || game.status === 'completed') && (
          <Section header={t('secretSanta.messages.title')}>
            <div className="flex flex-col gap-2 p-3">
              {messages.length === 0 ? (
                <Caption className="text-color-hint text-center p-4">
                  {t('secretSanta.messages.emptyState')}
                </Caption>
              ) : (
                messages.map((message) => {
                  const isFromMe = message.sender_id === authUser?.id;
                  return (
                    <div
                      key={message.id}
                      className={`flex flex-col gap-1 ${isFromMe ? 'items-end' : 'items-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          isFromMe ? 'bg-blue-500 text-white' : 'bg-section'
                        }`}
                      >
                        <Caption className={isFromMe ? 'text-white' : ''}>
                          {message.content}
                        </Caption>
                      </div>
                      <Caption className="text-color-hint text-xs">
                        {message.sent_at.toLocaleTimeString(language, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Caption>
                    </div>
                  );
                })
              )}
            </div>
            <div className="flex gap-2 p-3">
              <Input
                placeholder={t('secretSanta.messages.placeholder')}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="flex-1"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isSendingMessage) {
                    handleSendMessage();
                  }
                }}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!messageText.trim() || isSendingMessage}
                loading={isSendingMessage}
                before={<TbSend size={20} />}
              >
                {t('secretSanta.messages.send')}
              </Button>
            </div>
          </Section>
        )}
      </div>

      <Modal
        header={<ModalHeader>
          {selectedParticipant?.display_name || 'empty participant'
          }</ModalHeader>}
        open={selectedParticipant !== null}
        onOpenChange={bottomSheetOpenChange}
        className={"p-3 flex items-center "}
        style={{
          backgroundColor: (isDark ? "var(--tgui--section_bg_color)" : "var(--tgui--secondary_bg_color)"),
          maxHeight: "calc(100% - 50px)",
        }}
      >
        <Blockquote type="text" className={"description"}>
              {selectedParticipant?.preferences ?
                renderWithHtml(selectedParticipant?.preferences) :
                "("+t('secretSanta.assignment.noPreferences')+")"}
        </Blockquote>

        {selectedParticipant?.wishlist_id && (
          <Button
            stretched={true}
            onClick={() => {
              navigateToRoute(`/main/wishlists/${selectedParticipant?.wishlist_id}`);
            }}
            mode="plain"
            before={<LuList size={20} />}
            className={"mb-2"}
          >
            {t('secretSanta.assignment.viewWishlist')}
          </Button>
        )}
        <div className={"mt-10"}>
          {selectedParticipant?.user_id === authUser?.id && <div>
            <Button
              stretched={true}
              className="mb-2"
              onClick={() => {
                navigateToRoute(`/main/secret-santa/${game.id}/join`);
              }}
              before={<TbEdit size={20} />}
              mode="gray"
            >
              {t('secretSanta.participants.edit')}
            </Button>

            <Button
              stretched={true}
              className="mb-1"
              onClick={handleLeaveGame}
              before={<TbDoorExit size={20} />}
              mode="plain"
            >
              {t('secretSanta.participants.leave')}
            </Button>
          </div>}
          {selectedParticipant?.user_id !== authUser?.id && <Button
              stretched={true}
              className="mb-2 text-color-destroy"
              onClick={kickSelectedParticipant}
              before={<AiOutlineUserDelete size={20} />}
              mode="plain"
            >
              {t('secretSanta.participants.kickParticipant')}
          </Button>}
        </div>
      </Modal>
    </Page>
  );
};
